import { memo, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';

import { Disclosure } from '@headlessui/react';
import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCopy } from '@components/icons/copy';
import { IconDropRight } from '@components/icons/dropright';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenPreview } from '@components/icons/open-preview';
import { IconPlay } from '@components/icons/play';
import { IconShare } from '@components/icons/share';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { IconTrash } from '@components/icons/trash';
import { IconTrigger } from '@components/icons/trigger';
import { Menu } from '@components/menu';
import { forceHideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { capitalizeFirstLetter } from '@utils/strings';

import {
  useDeleteJourneyForm,
  useDuplicateJourneyForm,
  usePublishJourneyForm,
  useUnpublishJourneyForm,
  useUpdateJourneyForm,
} from 'api/journey-forms/hooks';
import { JourneyForm } from 'api/journey-forms/requests';
import {
  useDeleteJourneyPollingConfig,
  usePollNowJourneyPollingConfig,
  useUpdateJourneyPollingConfig,
} from 'api/journey-polling/hooks';
import { PollingConfig, PollingConfigList } from 'api/journey-polling/requests';
import { useDeleteJourneyWebhook, useUpdateJourneyWebhook } from 'api/journey-webhooks/hooks';
import { JourneyWebhook, JourneyWebhookList } from 'api/journey-webhooks/requests';
import { useJourney } from 'api/journeys/hooks';
import { JourneyType } from 'api/types';
import { FavoriteMenuItem } from 'ui/authenticated/main/favorites/favorites';
import { useShareJourneyFormModal } from 'ui/authenticated/main/pages/journeys/share-journey-form';
import { useJourneyContext } from 'ui/journey-builder/base-builder';
import { NodeContainer } from 'ui/journey-builder/node-types/node-container';
import { JourneyNode } from 'ui/journey-builder/node-types/types';
import { PreviewContext } from 'ui/journey-builder/preview-journey-form';

import {
  JourneyFormTriggerIcon,
  ManualTriggerIcon,
  ScheduledPollingTriggerIcon,
  TriggerCard,
  TriggerIcon,
  useTriggersCollapsedStore,
  WebhookTriggerIcon,
} from './common';
import { clearFormParam, setFormParam } from './form-url-params';
import { useEditJourneyFormOverlay } from './journey-form/edit-journey-form-overlay';
import { useAddTravellersSidebar } from './sidebars/add-trigger-sidebar';
import { useManualSidebar } from './sidebars/manual-sidebar';
import { useScheduledPollingSidebar } from './sidebars/scheduled-polling';
import { useWebhookSidebar } from './sidebars/webhook-sidebar';

import { ACTION_TYPES } from '../action-config';
import { DelayedCollapse } from '../common';

const ACTION_TYPE = ACTION_TYPES.journey_triggers;

export const Node = memo((node: JourneyNode<typeof ACTION_TYPE>) => {
  const { journeyId = '' } = useParams();
  const journeyQuery = useJourney(journeyId);

  if (!journeyQuery.isSuccess) {
    return null;
  }

  return (
    <NodeContainer hideTargetHandle hideSourceHandle node={node} disableClick id="triggers">
      <JourneyTriggers journey={journeyQuery.data} />
      <TriggerIcon bottomPaddingOffset={8} />
    </NodeContainer>
  );
});

const JourneyTriggers = ({ journey }: { journey: JourneyType }) => {
  const { t } = useScopedTranslate(locale);
  const { setPreviewJourneyFormId } = useContext(PreviewContext);
  const { journeyForms, webhooks, pollingConfigs } = useJourneyContext();

  const { isCollapsed, toggleCollapsed } = useTriggersCollapsedStore();

  const showAddTravellersSidebar = useAddTravellersSidebar();
  const showManualSidebar = useManualSidebar();
  const showEditFormOverlay = useEditJourneyFormOverlay();
  const showWebhookSidebar = useWebhookSidebar();
  const showScheduledPollingSidebar = useScheduledPollingSidebar();

  const handleAddClick = async () => {
    showAddTravellersSidebar({ journeyShortId: journey.shortId });
  };

  const totalTriggers = journeyForms.length + webhooks.length + pollingConfigs.length + 1;

  return (
    <Disclosure defaultOpen={!isCollapsed}>
      {({ open }) => (
        <Container>
          <Stack orientation="horizontal" justify="space-between" align="center">
            <Stack orientation="horizontal" space={2}>
              <IconTrigger color="elementBlue100" />
              <Text font="bold16" text={t('triggers')} />
            </Stack>

            <Stack orientation="horizontal" space={2} align="center">
              <Badge id="numberOfTriggers" size="md" text={totalTriggers} />
              <Disclosure.Button
                onClick={toggleCollapsed}
                as={IconButton}
                variant="ghost-purple"
                icon={<IconDropRight rotateDegrees={open ? 90 : 0} />}
              />
            </Stack>
          </Stack>
          <DelayedCollapse isCollapsed={!open} delay={500}>
            <Text
              font="regular12"
              color="textSecondary"
              text={t('description')}
              style={{ marginBottom: theme.space[4] }}
            />
            <Stack space={2}>
              <TriggerCard
                icon={<ManualTriggerIcon />}
                title={t('manual')}
                description={t('manualDescription')}
                onClick={() => showManualSidebar({ journeyId: journey.shortId })}
              />

              {journeyForms.map((form) => {
                return (
                  <TriggerCard
                    data-testid="external-form"
                    key={form.shortId}
                    icon={<JourneyFormTriggerIcon />}
                    title={t('externalForm')}
                    description={form.name}
                    onClick={() => {
                      setFormParam(form.shortId);
                      showEditFormOverlay(
                        { journeyId: journey.shortId, formId: form.shortId },
                        { onHide: clearFormParam }
                      );
                    }}
                    actions={
                      <JourneyFormActions form={form} setPreviewingId={setPreviewJourneyFormId} />
                    }
                  />
                );
              })}

              {webhooks.map((webhook) => {
                return (
                  <TriggerCard
                    data-testid="webhook"
                    key={webhook.shortId}
                    icon={<WebhookTriggerIcon />}
                    title={capitalizeFirstLetter(t('webhook'))}
                    description={webhook.description}
                    actions={<JourneyWebhookActions webhook={webhook} />}
                    onClick={() => showWebhookSidebar({ webhookShortId: webhook.shortId })}
                  />
                );
              })}

              {pollingConfigs.map((pollingConfig) => (
                <TriggerCard
                  data-testid="scheduled-polling"
                  key={pollingConfig.shortId}
                  icon={<ScheduledPollingTriggerIcon />}
                  title={t('scheduledPolling')}
                  description={pollingConfig.description}
                  actions={
                    <JourneyPollingActions pollingConfig={pollingConfig} journey={journey} />
                  }
                  onClick={() =>
                    showScheduledPollingSidebar({
                      journeyShortId: pollingConfig.journeyShortId,
                      pollingConfigShortId: pollingConfig.shortId,
                    })
                  }
                />
              ))}
              <Stack orientation="horizontal" align="center" justify="center">
                <Button
                  color="textSecondary"
                  variant="ghost"
                  text={t('addTrigger')}
                  iconLeft={<IconAddPlus color="buttonIconPositive" />}
                  onClick={handleAddClick}
                />
              </Stack>
            </Stack>
          </DelayedCollapse>
        </Container>
      )}
    </Disclosure>
  );
};

type JourneyFormCardProps = {
  form: JourneyForm;
  setPreviewingId(formId: string): void;
};

export const JourneyFormActions = ({ form, setPreviewingId }: JourneyFormCardProps) => {
  const { t } = useScopedTranslate(locale);
  const showDelete = useDeleteModal();
  const deleteMutation = useDeleteJourneyForm(form);
  const publishMutation = usePublishJourneyForm(form);
  const unpublishMutation = useUnpublishJourneyForm(form);
  const updateMutation = useUpdateJourneyForm(form.shortId);
  const shareModal = useShareJourneyFormModal();
  const onShareClick = () => shareModal({ journeyForm: form });
  const showEditFormOverlay = useEditJourneyFormOverlay();
  const duplicateMutation = useDuplicateJourneyForm(form);

  const handleToggle = (key: keyof JourneyForm, value: boolean) => {
    updateMutation.mutate({ ...form, [key]: value });
  };

  const handleEditClick = () => {
    setFormParam(form.shortId);
    showEditFormOverlay(
      { journeyId: form.journey, formId: form.shortId },
      { onHide: clearFormParam }
    );
  };

  const badges = [
    {
      enabled: form.isAutoStart,
      Icon: IconTrigger,
      getTooltipKey: (enabled: boolean) => (enabled ? 'autoStartEnabled' : 'autoStartDisabled'),
      flag: 'isAutoStart',
    },
    {
      enabled: form.isPublished,
      Icon: IconStatusIndicator,
      getTooltipKey: (enabled: boolean) => (enabled ? 'active' : 'disabled'),
      flag: 'isPublished',
    },
  ];

  return (
    <Stack orientation="horizontal" align="center" space={2} onClick={(e) => e.stopPropagation()}>
      <Stack orientation="horizontal" space={1}>
        {badges.map(({ enabled, Icon, getTooltipKey, flag }) => (
          <Badge
            key={flag}
            size="md"
            variant={enabled ? 'green' : 'neutral'}
            icon={
              <Icon
                color={enabled ? 'elementGreen200' : 'buttonIconPositive'}
                size={16}
                tooltip={t(getTooltipKey(enabled))}
              />
            }
          />
        ))}
      </Stack>

      <Menu
        buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}
        placement="left-start"
      >
        <Menu.Item label={t('core.edit')} icon={<IconEdit />} onSelect={handleEditClick} />
        <Menu.Item
          label={t('preview')}
          icon={<IconOpenPreview />}
          onSelect={() => setPreviewingId(form.shortId)}
        />
        <FavoriteMenuItem shortId={form.shortId} contentType="journeyform" />
        <Menu.Item
          icon={<IconShare />}
          label={t('share')}
          onSelect={onShareClick}
          isDisabled={!form.isPublished}
        />
        <Menu.Item
          icon={<IconCopy />}
          label={t('core.duplicate')}
          onSelect={() => duplicateMutation.mutateAsync()}
        />
        <Menu.Separator />
        <Menu.Switch
          label={t('active')}
          value={form.isPublished}
          onChange={() => {
            return form.isPublished
              ? unpublishMutation.mutateAsync()
              : publishMutation.mutateAsync();
          }}
        />
        <Menu.Switch
          label={t('startOnSubmission')}
          value={form.isAutoStart}
          onChange={() => handleToggle('isAutoStart', !form.isAutoStart)}
        />
        <Menu.Separator />
        <Menu.Item
          label={t('core.delete')}
          icon={<IconTrash />}
          variant="danger"
          onSelect={() =>
            showDelete({
              title: t('removeTitle', { name: form.name }),
              description: t('removeDescription', { name: form.name }),
              buttonText: t('core.delete'),
              hasUserConfirmation: true,
              onConfirm: deleteMutation.mutateAsync,
            })
          }
        />
      </Menu>
    </Stack>
  );
};

type JourneyWebhookCardProps = {
  webhook: JourneyWebhookList;
};

export const JourneyWebhookActions = ({ webhook }: JourneyWebhookCardProps) => {
  const { t } = useScopedTranslate(locale);
  const showDelete = useDeleteModal();
  const updateMutation = useUpdateJourneyWebhook(webhook);
  const deleteMutation = useDeleteJourneyWebhook(webhook.journeyShortId);
  const showWebhookSidebar = useWebhookSidebar();

  const handleToggle = (key: keyof JourneyWebhook, value: boolean) => {
    updateMutation.mutate({ [key]: value });
  };

  const badges = [
    {
      enabled: webhook.isAutoStart,
      Icon: IconTrigger,
      getTooltipKey: (enabled: boolean) => (enabled ? 'autoStartEnabled' : 'autoStartDisabled'),
      flag: 'isAutoStart',
    },
    {
      enabled: webhook.isActive,
      Icon: IconStatusIndicator,
      getTooltipKey: (enabled: boolean) => (enabled ? 'active' : 'disabled'),
      flag: 'isActive',
    },
  ];

  return (
    <Stack orientation="horizontal" align="center" space={2}>
      <Stack orientation="horizontal" space={1}>
        {badges.map(({ enabled, Icon, getTooltipKey, flag }) => (
          <Badge
            key={flag}
            size="md"
            variant={enabled ? 'green' : 'neutral'}
            icon={
              <Icon
                color={enabled ? 'elementGreen200' : 'buttonIconPositive'}
                size={16}
                tooltip={t(getTooltipKey(enabled))}
              />
            }
          />
        ))}
      </Stack>

      <Menu
        buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}
        placement="left-start"
      >
        <Menu.Item
          label={t('core.edit')}
          icon={<IconEdit />}
          onSelect={() => showWebhookSidebar({ webhookShortId: webhook.shortId })}
        />
        <Menu.Separator />

        <Menu.Switch
          label={t('active')}
          value={webhook.isActive}
          onChange={() => handleToggle('isActive', !webhook.isActive)}
        />
        <Menu.Switch
          label={t('startOnSubmission')}
          value={webhook.isAutoStart}
          onChange={() => handleToggle('isAutoStart', !webhook.isAutoStart)}
        />

        <Menu.Separator />
        <Menu.Item
          label={t('core.delete')}
          icon={<IconTrash />}
          variant="danger"
          onSelect={() =>
            showDelete({
              title: t('removeTitle', { name: t('webhook') }),
              description: t('removeDescription', { name: t('webhook') }),
              buttonText: t('core.delete'),
              hasUserConfirmation: true,
              onConfirm: () =>
                deleteMutation.mutateAsync(webhook.shortId, { onSuccess: forceHideSidebar }),
            })
          }
        />
      </Menu>
    </Stack>
  );
};

export const JourneyPollingActions = ({
  pollingConfig,
  journey,
}: {
  pollingConfig: PollingConfigList;
  journey: JourneyType;
}) => {
  const { t } = useScopedTranslate(locale);
  const showDelete = useDeleteModal();
  const updateMutation = useUpdateJourneyPollingConfig(pollingConfig);
  const deleteMutation = useDeleteJourneyPollingConfig(pollingConfig.journeyShortId);
  const pollNowMutation = usePollNowJourneyPollingConfig(
    pollingConfig.shortId,
    pollingConfig.journeyShortId,
    journey.workspace
  );
  const showScheduledPollingSidebar = useScheduledPollingSidebar();

  const handleToggle = (key: keyof PollingConfig, value: boolean) => {
    updateMutation.mutate({ [key]: value });
  };

  const badges = [
    {
      enabled: pollingConfig.isAutoStart,
      Icon: IconTrigger,
      getTooltipKey: (enabled: boolean) => (enabled ? 'autoStartEnabled' : 'autoStartDisabled'),
      flag: 'isAutoStart',
    },
    {
      enabled: pollingConfig.isActive,
      Icon: IconStatusIndicator,
      getTooltipKey: (enabled: boolean) => (enabled ? 'active' : 'disabled'),
      flag: 'isActive',
    },
  ];

  const handleFetchNow = useCallback(async () => {
    if (!pollNowMutation.isLoading) {
      await pollNowMutation.mutateAsync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack orientation="horizontal" align="center" space={2}>
      <Stack orientation="horizontal" space={1}>
        {badges.map(({ enabled, Icon, getTooltipKey, flag }) => (
          <Badge
            key={flag}
            size="md"
            variant={enabled ? 'green' : 'neutral'}
            icon={
              <Icon
                color={enabled ? 'elementGreen200' : 'buttonIconPositive'}
                size={16}
                tooltip={t(getTooltipKey(enabled))}
              />
            }
          />
        ))}
      </Stack>
      <Menu
        buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}
        placement="left-start"
      >
        <Menu.Item
          label={t('core.edit')}
          icon={<IconEdit />}
          onSelect={() =>
            showScheduledPollingSidebar({
              journeyShortId: pollingConfig.journeyShortId,
              pollingConfigShortId: pollingConfig.shortId,
            })
          }
        />
        <Menu.Item
          label={t('fetchNow')}
          icon={<IconPlay />}
          onSelect={handleFetchNow}
          isDisabled={!pollingConfig.isActive || !journey.hasPublishedJourney}
          tooltip={
            !pollingConfig.isActive
              ? t('fetchNowDisabledPollingInactive')
              : !journey.hasPublishedJourney
              ? t('fetchNowDisabledJourneyNotPublished')
              : undefined
          }
        />
        <Menu.Separator />
        <Menu.Switch
          label={t('active')}
          value={pollingConfig.isActive}
          onChange={() => handleToggle('isActive', !pollingConfig.isActive)}
        />
        <Menu.Switch
          label={t('startOnSubmission')}
          value={pollingConfig.isAutoStart}
          onChange={() => handleToggle('isAutoStart', !pollingConfig.isAutoStart)}
        />
        <Menu.Separator />
        <Menu.Item
          label={t('core.delete')}
          icon={<IconTrash />}
          variant="danger"
          onSelect={() =>
            showDelete({
              title: t('deletePolling'),
              description: t('deletePollingDescription'),
              buttonText: t('core.delete'),
              hasUserConfirmation: true,
              onConfirm: () =>
                deleteMutation.mutateAsync(pollingConfig.shortId, { onSuccess: forceHideSidebar }),
            })
          }
        />
      </Menu>
    </Stack>
  );
};

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: pxToRem(354),
  padding: pxToRem(16),
  gap: theme.space[4],
  borderRadius: pxToRem(8),
  border: `1px solid ${theme.color.border10}`,
  backgroundColor: theme.color.backgroundLightDark,
});

const locale = {
  en: {
    triggers: 'Triggers',
    description: 'Triggers are how people are added to your journey',
    manual: 'Manually added',
    manualDescription: 'Always available',
    addTrigger: 'Add trigger',
    externalForm: 'External form',
    rename: 'Rename',
    preview: 'Preview',
    active: 'Active',
    disabled: 'Disabled',
    share: 'Share',
    startOnSubmission: 'Start on submission',
    autoStartEnabled: 'Auto start enabled',
    autoStartDisabled: 'Auto start disabled',
    webhook: 'webhook',
    form: 'form',
    removeTitle: 'Delete external form',
    removeDescription: 'Are you sure you want to delete the external form: {name}?',
    deletePolling: 'Delete scheduled polling',
    deletePollingDescription: 'Are you sure you want to delete the scheduled polling?',
    fetchNow: 'Fetch now • Manual',
    scheduledPolling: 'Scheduled polling',
    fetchNowDisabledPollingInactive: 'Polling is not active',
    fetchNowDisabledJourneyNotPublished: 'Journey is not published',
  },
};
